// ============================================================================
// CONFIGURATION CENTRALISÉE DES OUTILS MOPPER
// Toutes les constantes métier (volumes moyens, règles cartons, véhicules)
// vivent ICI et nulle part ailleurs. Ne pas dupliquer ces valeurs dans les
// composants. Volumes en m³, indicatifs et non contractuels.
// ============================================================================

// Pièces proposées (clés stables ; libellés traduits dans toolsI18n.js)
export const ROOM_KEYS = [
  "salon", "cuisine", "chambre", "chambre-enfant", "bureau",
  "sdb", "cave", "grenier", "garage", "balcon", "autre",
];

// Catégories d'objets (pour filtres inventaire ; libellés dans toolsI18n)
export const CATEGORIES = ["meuble", "electro", "literie", "carton", "deco", "divers"];

// Catalogue d'objets courants. v = volume moyen m³. rooms = pièces où l'objet
// est proposé dans le calculateur. cat = catégorie inventaire.
export const OBJECT_CATALOG = [
  // Salon
  { id: "canape2", v: 1.0, cat: "meuble", rooms: ["salon"] },
  { id: "canape3", v: 1.5, cat: "meuble", rooms: ["salon"] },
  { id: "canape_angle", v: 2.2, cat: "meuble", rooms: ["salon"] },
  { id: "fauteuil", v: 0.6, cat: "meuble", rooms: ["salon", "chambre", "bureau"] },
  { id: "tablebasse", v: 0.35, cat: "meuble", rooms: ["salon"] },
  { id: "tvmeuble", v: 0.5, cat: "meuble", rooms: ["salon"] },
  { id: "television", v: 0.25, cat: "electro", rooms: ["salon", "chambre"] },
  { id: "bibli", v: 0.8, cat: "meuble", rooms: ["salon", "bureau"] },
  { id: "buffet", v: 1.1, cat: "meuble", rooms: ["salon"] },
  { id: "table", v: 0.8, cat: "meuble", rooms: ["salon", "cuisine"] },
  { id: "chaise", v: 0.2, cat: "meuble", rooms: ["salon", "cuisine", "bureau"] },
  // Chambre
  { id: "litsimple", v: 0.9, cat: "literie", rooms: ["chambre", "chambre-enfant"] },
  { id: "litdouble", v: 1.3, cat: "literie", rooms: ["chambre"] },
  { id: "matelas", v: 0.8, cat: "literie", rooms: ["chambre", "chambre-enfant"] },
  { id: "armoire2", v: 1.2, cat: "meuble", rooms: ["chambre", "chambre-enfant"] },
  { id: "armoire3", v: 1.8, cat: "meuble", rooms: ["chambre"] },
  { id: "commode", v: 0.5, cat: "meuble", rooms: ["chambre", "chambre-enfant"] },
  { id: "tabledenuit", v: 0.15, cat: "meuble", rooms: ["chambre"] },
  { id: "bureau", v: 0.6, cat: "meuble", rooms: ["chambre", "bureau", "chambre-enfant"] },
  // Chambre enfant
  { id: "lit_enfant", v: 0.7, cat: "literie", rooms: ["chambre-enfant"] },
  { id: "coffre_jouets", v: 0.3, cat: "divers", rooms: ["chambre-enfant"] },
  // Cuisine & électro
  { id: "frigo", v: 0.6, cat: "electro", rooms: ["cuisine"] },
  { id: "congelateur", v: 0.55, cat: "electro", rooms: ["cuisine", "cave"] },
  { id: "four", v: 0.4, cat: "electro", rooms: ["cuisine"] },
  { id: "lavevaisselle", v: 0.5, cat: "electro", rooms: ["cuisine"] },
  { id: "meublecuis", v: 0.5, cat: "meuble", rooms: ["cuisine"] },
  // Électroménager (buanderie / sdb / cave)
  { id: "lavelinge", v: 0.5, cat: "electro", rooms: ["sdb", "cuisine", "cave"] },
  { id: "sechelinge", v: 0.5, cat: "electro", rooms: ["sdb", "cave"] },
  // Bureau
  { id: "caisson", v: 0.2, cat: "meuble", rooms: ["bureau"] },
  // Divers / cave / grenier / garage / balcon
  { id: "etagere", v: 0.4, cat: "meuble", rooms: ["cave", "grenier", "garage", "salon", "bureau"] },
  { id: "velo", v: 0.5, cat: "divers", rooms: ["garage", "cave", "balcon"] },
  { id: "plante", v: 0.15, cat: "deco", rooms: ["salon", "balcon"] },
  { id: "salon_jardin", v: 1.4, cat: "meuble", rooms: ["balcon"] },
  { id: "objet_volumineux", v: 0.8, cat: "divers", rooms: ["cave", "grenier", "garage", "autre"] },
  // Cartons (proposés partout)
  { id: "cartonstd", v: 0.06, cat: "carton", rooms: ROOM_KEYS },
  { id: "cartonlivre", v: 0.05, cat: "carton", rooms: ROOM_KEYS },
  { id: "penderie", v: 0.12, cat: "carton", rooms: ["chambre", "chambre-enfant"] },
];

// Marge de préparation appliquée au volume brut (emballage, jeu de chargement).
export const VOLUME_MARGIN = 1.1;

export function objectById(id) {
  return OBJECT_CATALOG.find((o) => o.id === id) || null;
}
export function objectsForRoom(roomKey) {
  return OBJECT_CATALOG.filter((o) => o.rooms.includes(roomKey));
}

// Volume d'un objet personnalisé (dimensions en cm) → m³.
export function customVolumeM3(lengthCm, widthCm, heightCm, qty = 1) {
  const l = Math.max(0, Number(lengthCm) || 0);
  const w = Math.max(0, Number(widthCm) || 0);
  const h = Math.max(0, Number(heightCm) || 0);
  const q = Math.max(1, Number(qty) || 1);
  return (l * w * h) / 1_000_000 * q; // cm³ → m³
}

// ---- VÉHICULES (indicatif) --------------------------------------------------
export const VEHICLE_RULES = [
  { max: 6, key: "small" },
  { max: 12, key: "m2" },
  { max: 22, key: "m3" },
  { max: Infinity, key: "big" },
];
export function vehicleForVolume(v) {
  if (!v || v <= 0) return null;
  return VEHICLE_RULES.find((r) => v <= r.max) || null;
}

// ---- RÈGLES CARTONS ---------------------------------------------------------
// base = nombre de cartons "standard-équivalents" de référence par logement.
export const BOX_RULES = {
  base: { studio: 15, "2p": 25, "3p": 40, "4p": 55, "5p": 72, maison: 95 },
  // multiplicateur par nombre de personnes (léger)
  persons: { 1: 0.85, 2: 1.0, 3: 1.12, 4: 1.22, 5: 1.32 },
  // niveau d'encombrement
  clutter: { peu: 0.75, normal: 1.0, beaucoup: 1.35 },
  // extras (cartons ajoutés) si l'utilisateur signale "beaucoup de …"
  extras: { livres: 6, vetements: 5, vaisselle: 5, deco: 4, documents: 3, jouets: 4 },
  // répartition indicative par type (part du total)
  breakdown: { standard: 0.5, petits: 0.12, livres: 0.16, fragiles: 0.14, penderies: 0.08 },
  // fourchette affichée = total ± spread
  spread: 0.12,
};

export const HOUSING_KEYS = ["studio", "2p", "3p", "4p", "5p", "maison"];
export const PERSON_KEYS = [1, 2, 3, 4, 5];
export const CLUTTER_KEYS = ["peu", "normal", "beaucoup"];
export const EXTRA_KEYS = ["livres", "vetements", "vaisselle", "deco", "documents", "jouets"];

// Calcul centralisé du nombre de cartons.
export function computeBoxes(inputs) {
  const { housing = "3p", persons = 2, clutter = "normal", extras = [] } = inputs || {};
  const base = BOX_RULES.base[housing] ?? BOX_RULES.base["3p"];
  const pMul = BOX_RULES.persons[persons] ?? 1.0;
  const cMul = BOX_RULES.clutter[clutter] ?? 1.0;
  let total = base * pMul * cMul;
  for (const e of extras) total += BOX_RULES.extras[e] || 0;
  total = Math.round(total);
  const min = Math.max(1, Math.round(total * (1 - BOX_RULES.spread)));
  const max = Math.round(total * (1 + BOX_RULES.spread));
  const br = BOX_RULES.breakdown;
  const breakdown = {
    standard: Math.round(total * br.standard),
    petits: Math.round(total * br.petits),
    livres: Math.round(total * br.livres),
    fragiles: Math.round(total * br.fragiles),
    penderies: Math.max(1, Math.round(total * br.penderies)),
  };
  return { total, min, max, breakdown };
}

// ---- INVENTAIRE -------------------------------------------------------------
export const CONTAINER_TYPES = ["carton", "caisse", "bac", "valise", "autre"];
export const ITEM_ACTIONS = ["demenager", "garder", "donner", "vendre", "recycler", "jeter"];
